import { GiftAddressRegion } from '@shared/api/giftOrder/giftOrder.interface';
import type { Address } from 'react-daum-postcode';

export const EMPTY_ADDRESS_REGION: GiftAddressRegion = {};

/**
 * 카카오(Daum) 우편번호 응답에서 권역 매칭용 6필드를 원문 그대로 뽑는다.
 * 정규화(서울특별시→서울 등)는 서버가 하므로 여기서 다듬지 않는다.
 */
export const regionFromDaumAddress = (data: Address): GiftAddressRegion => ({
  sido: data.sido || undefined,
  sigungu: data.sigungu || undefined,
  bname: data.bname || undefined,
  bname1: data.bname1 || undefined,
  hname: data.hname || undefined,
  bcode: data.bcode || undefined,
});

/** 시도 + (행정동|법정동|읍면) 중 하나가 있어야 서버가 읍면동을 판정할 수 있다 */
export const hasRegionInfo = (region?: GiftAddressRegion | null): boolean =>
  !!region?.sido && !!(region.hname || region.bname || region.bname1);

/** 비교/저장용 — undefined 필드는 빼고 값 있는 것만 */
export const pickRegion = (
  source?: Partial<GiftAddressRegion> | null,
): GiftAddressRegion | undefined => {
  if (!source) return undefined;
  const region: GiftAddressRegion = {};
  (['sido', 'sigungu', 'bname', 'bname1', 'hname', 'bcode'] as const).forEach((key) => {
    const value = source[key];
    if (typeof value === 'string' && value) region[key] = value;
  });
  return Object.keys(region).length > 0 ? region : undefined;
};

export const isSameRegion = (
  a?: GiftAddressRegion | null,
  b?: GiftAddressRegion | null,
): boolean => {
  const pa = pickRegion(a);
  const pb = pickRegion(b);
  if (!pa && !pb) return true;
  if (!pa || !pb) return false;
  return (['sido', 'sigungu', 'bname', 'bname1', 'hname', 'bcode'] as const).every(
    (key) => pa[key] === pb[key],
  );
};

/**
 * 수령인(배송지) 수정 PATCH에 실을 카카오 원문 필드.
 * 주소를 재검색한 경우에만 region이 있고, 그때만 권역 배송비 근거(시도·읍면동)가 함께 갱신된다.
 * 재검색하지 않았으면 빈 객체 → 기존 저장값을 건드리지 않는다.
 * 사용: `updateGiftDeliveryRecipient(id, { ...fields, ...recipientRegionPatch(modal.region) })`
 */
export const recipientRegionPatch = (
  region?: GiftAddressRegion | null,
): GiftAddressRegion => region ?? {};
